import { RouteAccess } from '@/app/RouteAccess';
import { AssistantHost, DockedAssistant } from '@/assistant/AssistantHost';
import { cx } from '@/lib/cx';
import { useElementWidth, useHotkey, useLocalStorage } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { LayoutContext, layoutFor } from '@/ui';
import s from './AppShell.module.css';
import { BottomTabs } from './BottomTabs';
import { RealtimeProvider } from './realtime';
import { SearchPalette } from './SearchPalette';
import { openSearch } from './searchStore';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

/**
 * The shell: three layouts from one codebase, chosen by the shell's own width (IA §4).
 *   phone < 640      top bar · page · bottom tab bar; assistant as a bottom sheet
 *   laptop 640–1919  collapsible sidebar (232 / 64 px) · page; assistant slides over
 *   ultrawide ≥ 1920 expanded sidebar · page · docked assistant column (420 px)
 */
export function AppShell() {
  const [ref, width] = useElementWidth();
  const layout = layoutFor(width || (typeof window !== 'undefined' ? window.innerWidth : 1440));
  const [collapsed, setCollapsed] = useLocalStorage('nova.nav.collapsed', false);
  useHotkey('mod+k', () => openSearch());

  return (
    <LayoutContext.Provider value={layout}>
      <RealtimeProvider>
        <div ref={ref} className={cx(s.shell, s[layout])} data-layout={layout}>
          <a href="#main" className={s.skip}>
            {t('Skip to content')}
          </a>
          {layout !== 'phone' && (
            <Sidebar collapsed={layout === 'laptop' && collapsed} onToggle={layout === 'laptop' ? () => setCollapsed(!collapsed) : undefined} />
          )}
          <div className={s.main}>
            <TopBar />
            <main id="main" className={s.page} tabIndex={-1}>
              <RouteAccess />
            </main>
            {layout === 'phone' && <BottomTabs />}
          </div>
          {layout === 'ultrawide' && <DockedAssistant />}
          <AssistantHost />
          <SearchPalette />
        </div>
      </RealtimeProvider>
    </LayoutContext.Provider>
  );
}
